import { poseFromPoseFrames } from './format'
import {
  addStoryMs,
  compareStory,
  inStoryWindow,
  isoFromStoryMs,
  storyDeltaMs,
  storyMs,
} from './storyTime'
import { defaultObjectDescription } from './objectMeta'
import { isBrwnPlumeType } from './eggs/brwnPlume'
import { objectShowsOnTimeline, paletteLabel } from './palette'
import { profileFieldsFromBlob, readRegistrationProfileJson } from './user'
import { typeOffersStoryClock } from './sessionTypes'
import { sampleWindAt } from './windInterp'
import type { BoardRevealMode } from './boardReveal'
import type {
  ResolvedObject,
  ResolvedSession,
  ScenarioObject,
  ScenarioPose,
  AccountBoardMode,
  ScenarioSession,
  TimelineEvent,
  VisibilityKeyframe,
} from './types'

/** Sample an object's pose at a story playhead (continuous for airplane/helicopter/drone/tornado tracks). */
export function poseFromKeyframes(obj: ScenarioObject, playheadIso: string): ScenarioPose | null {
  const frames = obj.keyframes
  if (!frames?.length) return obj.pose
  // Aircraft (and tornado tracks) lerp the whole gap - no "hold at pin until 40 mph depart".
  const motion =
    isLocalStorm(obj.type) || obj.type === 'airplane' || obj.type === 'helicopter' || obj.type === 'drone'
      ? 'continuous'
      : 'arrive-on-time'
  return poseFromPoseFrames(frames, playheadIso, Boolean(obj.loopKeyframes), motion)
}

function applyScheduleCycle(
  obj: ScenarioObject,
  elapsedMs: number,
  params: Record<string, unknown>,
): Record<string, unknown> {
  if (obj.type === 'drawbridge') {
    const openMs = Number(params.cycleOpenMs)
    const closedMs = Number(params.cycleClosedMs)
    if (!Number.isFinite(openMs) || !Number.isFinite(closedMs) || openMs + closedMs <= 0) {
      return params
    }
    const period = openMs + closedMs
    const inCycle = elapsedMs % period
    return { ...params, deckStatus: inCycle < openMs ? 'open' : 'closed' }
  }
  if (obj.type === 'truck') {
    const blockedMs = Number(params.cycleBlockedMs)
    const clearMs = Number(params.cycleClearMs)
    if (!Number.isFinite(blockedMs) || !Number.isFinite(clearMs) || blockedMs + clearMs <= 0) {
      return params
    }
    const period = blockedMs + clearMs
    const inCycle = elapsedMs % period
    return { ...params, blocked: inCycle < blockedMs }
  }
  return params
}

export function isObjectVisible(obj: ScenarioObject, playheadIso: string): boolean {
  if (!inStoryWindow(playheadIso, obj.startsAt, obj.endsAt)) return false
  const frames = obj.visibilityKeyframes
  if (frames?.length) {
    const sorted = [...frames].sort((a, b) => compareStory(a.at, b.at))
    const first = sorted[0]!
    let visible = !first.visible
    for (const frame of sorted) {
      if (compareStory(frame.at, playheadIso) <= 0) visible = frame.visible
      else break
    }
    return visible
  }
  return true
}

/** Promote a show/hide pair into visibility keyframes. Presence bounds stay on the object. */
export function visibilityFramesFromShowHide(obj: ScenarioObject): VisibilityKeyframe[] {
  const frames: VisibilityKeyframe[] = []
  if (obj.startsAt) frames.push({ at: obj.startsAt, visible: true })
  if (obj.endsAt) frames.push({ at: obj.endsAt, visible: false })
  return frames.sort((a, b) => compareStory(a.at, b.at))
}

export function resolveSession(
  session: ScenarioSession,
  playheadIso: string,
  accountBoardMode: AccountBoardMode,
  revealMode: BoardRevealMode = accountBoardMode === 'following' ? 'audience' : 'author',
  /** Hold-to-reveal: include everything this view would hide, as ghosts. */
  flashHidden = false,
): ResolvedSession {
  const elapsedMs = Math.max(0, storyDeltaMs(session.storyStartTime, playheadIso))
  const timeline = mergeTimeline(session)
  const firedEvents = timeline.filter((e) => compareStory(e.at, playheadIso) <= 0)
  const upcomingEvents = timeline.filter((e) => compareStory(e.at, playheadIso) > 0)

  const paramOverrides = new Map<string, Record<string, unknown>>()
  for (const ev of firedEvents) {
    for (const action of ev.actions) {
      if (action.kind !== 'setParams') continue
      paramOverrides.set(action.objectId, {
        ...(paramOverrides.get(action.objectId) ?? {}),
        ...action.params,
      })
    }
  }

  const asAudience = revealMode === 'audience' || accountBoardMode === 'following'
  const showParked = revealMode === 'inventory' && !asAudience
  /** Authors only. Overrides Show on board, inactive, and show/hide for this frame. */
  const revealAll = flashHidden && accountBoardMode !== 'following'

  const objects: ResolvedObject[] = []
  for (const obj of session.objects) {
    // Inactive objects stay off the map unless this view is the full inventory.
    // Inventory still uses the clock: in-window objects draw normally (plume included);
    // outside the window they are ghosts, same as any other author object.
    if (obj.ignored && !showParked && !revealAll) continue

    const visible = isObjectVisible(obj, playheadIso)
    if (!visible && asAudience && !revealAll) continue

    let params = { ...obj.params }
    params = applyScheduleCycle(obj, elapsedMs, params)
    if (obj.radiusKeyframes?.length) {
      let radius = Number(params.radiusM)
      const frames = [...obj.radiusKeyframes].sort((a, b) => compareStory(a.at, b.at))
      for (const frame of frames) {
        if (compareStory(frame.at, playheadIso) <= 0) radius = frame.value
      }
      if (Number.isFinite(radius)) params = { ...params, radiusM: radius }
    }
    if (obj.statusKeyframes?.length) {
      let status = String(params.status ?? 'open')
      const frames = [...obj.statusKeyframes].sort((a, b) => compareStory(a.at, b.at))
      for (const frame of frames) {
        if (compareStory(frame.at, playheadIso) <= 0) status = frame.status
      }
      params = { ...params, status }
    }
    if (
      typeOffersStoryClock(session.sessionType) &&
      (obj.type === 'wind' || isLocalStorm(obj.type)) &&
      obj.windKeyframes?.length
    ) {
      const sample = sampleWindAt(obj.windKeyframes, playheadIso)
      if (sample) {
        params = {
          ...params,
          fromDeg: sample.fromDeg,
          speedMph: sample.speedMph,
          plumeDistanceM: sample.plumeDistanceM,
          plumeSpreadDeg: sample.plumeSpreadDeg,
        }
      }
    }
    const over = paramOverrides.get(obj.id)
    if (over) params = { ...params, ...over }

    // Beat size effects ride setParams → params.size; markers read ResolvedObject.size.
    const sizeOverride = params.size
    const size =
      sizeOverride === 'sm' ||
      sizeOverride === 'md' ||
      sizeOverride === 'lg' ||
      sizeOverride === 'xl'
        ? sizeOverride
        : obj.size

    const solid = visible && !(obj.ignored && !showParked)
    objects.push({
      ...obj,
      size,
      pose: poseFromKeyframes(obj, playheadIso),
      params,
      ghost: revealAll ? !solid : !visible && !asAudience,
      effective: {},
    })
  }

  const byId = new Map(objects.map((o) => [o.id, o]))
  for (const obj of objects) {
    if (obj.ghost) continue
    if (obj.type === 'drawbridge') {
      const roadClosed = (obj.relatedObjectIds ?? []).some((id) => {
        const rel = byId.get(id)
        return rel != null && !rel.ghost && String(rel.params.status) === 'closed'
      })
      obj.effective.deckStatus = roadClosed ? 'closed' : (obj.params.deckStatus ?? 'open')
      obj.effective.overrideReason = roadClosed ? 'Road closed — deck forced shut' : null
    }
    if (obj.type === 'truck') {
      const roadClosed = (obj.relatedObjectIds ?? []).some((id) => {
        const rel = byId.get(id)
        return rel != null && !rel.ghost && String(rel.params.status) === 'closed'
      })
      obj.effective.blocked = roadClosed ? true : Boolean(obj.params.blocked)
      obj.effective.overrideReason = roadClosed ? 'Road closed — truck held at the work zone' : null
      if (roadClosed) {
        const holdLng = Number(obj.params.holdLng)
        const holdLat = Number(obj.params.holdLat)
        if (Number.isFinite(holdLng) && Number.isFinite(holdLat)) {
          obj.pose = { lng: holdLng, lat: holdLat }
        }
      }
    }
    if (obj.type === 'road') {
      obj.effective.status = obj.params.status ?? 'open'
    }
  }

  // Among events that share the latest fire time (e.g. three cards at 0:00), use the
  // first in list order — not the last. Last-wins made "Surface wind" the caret when
  // opening the timeline at t=0 even though Lowboy / Scenario start come first.
  const latestAt = firedEvents.length ? firedEvents[firedEvents.length - 1]!.at : null
  const currentEvent =
    latestAt == null ? null : (firedEvents.find((e) => compareStory(e.at, latestAt) === 0) ?? null)
  return { objects, firedEvents, upcomingEvents, currentEvent }
}

export function mergeTimeline(session: ScenarioSession): TimelineEvent[] {
  // Persisted rows only — 1:1 object cards are written by healSyncedTimeline, not invented here.
  return [...session.timeline].sort(
    (a, b) => compareStory(a.at, b.at) || a.title.localeCompare(b.title),
  )
}

const SYNC_TIMELINE_PREFIX = 'sync-'

export function syncedTimelineEventId(objectId: string): string {
  return `${SYNC_TIMELINE_PREFIX}${objectId}`
}

export function isSyncedTimelineEvent(ev: { id: string }): boolean {
  return ev.id.startsWith(SYNC_TIMELINE_PREFIX)
}

/**
 * Story-owned glue / inject card (Focus #3).
 * New inserts set `kind: 'narration'`. Sync rows use id `sync-*`.
 * Legacy library templates often omit `kind` but still carry reveal/setParams —
 * those are authored story cards too (empty-action-only was too narrow and left
 * Bird strike non-editable while Mayday looked editable).
 */
export function isNarrationTimelineEvent(ev: TimelineEvent): boolean {
  if (ev.kind === 'narration') return true
  return !isSyncedTimelineEvent(ev)
}

/** Default gap when appending a beat after the last card. */
export const NARRATION_INSERT_STEP_MS = 10 * 60_000

/** Below this gap, insert reuses the previous stamp (no second-splitting). */
const NARRATION_INSERT_MIN_GAP_MS = 60_000

/**
 * Story ISO for an insert between neighbors.
 * Prefer +10 min from prev when it still fits; else nearest-minute midpoint;
 * if the window is tighter than a minute, reuse prev (same-stamp cluster).
 */
export function narrationBeatAtBetween(
  prevAt: string | null | undefined,
  nextAt: string | null | undefined,
  storyStartTime: string,
  storyEndTime?: string | null,
): string {
  const start = storyStartTime
  const end = storyEndTime?.trim() || addStoryMs(start, 60 * 60_000)
  const lo = prevAt?.trim() || start
  const hi = nextAt?.trim() || end
  const loMs = storyMs(lo)
  const hiMs = storyMs(hi)
  const gap = hiMs - loMs
  if (gap <= NARRATION_INSERT_MIN_GAP_MS) return lo

  const stepped = loMs + NARRATION_INSERT_STEP_MS
  if (stepped <= hiMs - NARRATION_INSERT_MIN_GAP_MS) {
    return isoFromStoryMs(stepped)
  }

  const mid = loMs + Math.round(gap / 2)
  const rounded = Math.round(mid / 60_000) * 60_000
  if (rounded > loMs && rounded < hiMs) return isoFromStoryMs(rounded)

  const nextMinute = Math.floor(loMs / 60_000) * 60_000 + 60_000
  if (nextMinute < hiMs) return isoFromStoryMs(nextMinute)
  return lo
}

export function buildNarrationTimelineEvent(opts: {
  at: string
  title?: string
  summary?: string
}): TimelineEvent {
  return {
    // Keep id generation local — avoid resolveSession ↔ persist ↔ canned cycles.
    id: `narration_${Math.random().toString(36).slice(2, 9)}`,
    at: opts.at,
    title: opts.title?.trim() || 'Story card',
    summary: opts.summary?.trim() || '',
    iconType: 'story_beat',
    hiddenFromParticipants: false,
    actions: [],
    kind: 'narration',
  }
}

/** Fresh insert with default title/empty body — hide further Adds until dirtied. */
export function isPristineNarrationBeat(ev: TimelineEvent): boolean {
  if (!isNarrationTimelineEvent(ev)) return false
  const title = ev.title.trim()
  return (!title || title === 'Story card' || title === 'Story beat') && !ev.summary.trim()
}

function objectTimelineAt(obj: ScenarioObject, storyStartTime: string): string | null {
  // Show-on-board From is the authored appear time — it wins over addedAt / keyframe mins.
  if (obj.startsAt) return obj.startsAt
  const times: string[] = []
  if (obj.addedAt) times.push(obj.addedAt)
  // Storm path and wind marks stay inside the story window. Moving this card
  // to the first mark made that clock time the end of both bars.
  if (!isLocalStorm(obj.type) && obj.keyframes?.length) times.push(obj.keyframes[0]!.at)
  if ((obj.radiusKeyframes?.length ?? 0) > 1) times.push(obj.radiusKeyframes![0]!.at)
  if (obj.statusKeyframes?.length) times.push(obj.statusKeyframes[0]!.at)
  if (obj.visibilityKeyframes?.length) times.push(obj.visibilityKeyframes[0]!.at)
  if (times.length) return times.sort(compareStory)[0]!
  if (objectShowsOnTimeline(obj.type, obj.showOnTimeline)) return storyStartTime
  return null
}

function objectTimelineSummary(obj: ScenarioObject, session: ScenarioSession): string {
  if (isBrwnPlumeType(obj.type)) {
    const first = profileFieldsFromBlob(readRegistrationProfileJson()).first
    return defaultObjectDescription(obj, {
      firstName: first,
      displayUnits: session.displayUnits ?? 'imperial',
    })
  }
  const authored = obj.description?.trim()
  if (authored) return authored
  return defaultObjectDescription(obj)
}

function buildSyncedTimelineEvent(obj: ScenarioObject, session: ScenarioSession): TimelineEvent | null {
  if (obj.ignored) return null
  if (!objectShowsOnTimeline(obj.type, obj.showOnTimeline)) return null
  const at = objectTimelineAt(obj, session.storyStartTime)
  if (at == null) return null
  return {
    id: syncedTimelineEventId(obj.id),
    at,
    title: obj.label.trim() || paletteLabel(obj.type),
    summary: objectTimelineSummary(obj, session),
    iconType: obj.type,
    hiddenFromParticipants: false,
    actions: [{ kind: 'reveal', objectId: obj.id }],
  }
}

/**
 * Persist 1:1 timeline rows for board objects with Include-as-story-card on.
 * Only `showOnTimeline` (Inspect) gates sync cards — narration effects must not
 * silently hide an object card. Authored story copy is left alone.
 */
export function healSyncedTimeline(session: ScenarioSession): ScenarioSession {
  const byId = new Map(session.objects.map((o) => [o.id, o]))
  const nextSync = new Map<string, TimelineEvent>()

  for (const obj of session.objects) {
    const ev = buildSyncedTimelineEvent(obj, session)
    if (ev) nextSync.set(obj.id, ev)
  }

  const kept: TimelineEvent[] = []
  const seenSync = new Set<string>()
  for (const ev of session.timeline) {
    if (!isSyncedTimelineEvent(ev)) {
      kept.push(ev)
      continue
    }
    const objectId = (ev.actions ?? []).find((a) => a.kind === 'reveal')?.objectId
    if (!objectId || !byId.has(objectId)) continue
    const fresh = nextSync.get(objectId)
    if (!fresh) continue
    kept.push(fresh)
    seenSync.add(objectId)
  }
  for (const [objectId, ev] of nextSync) {
    if (!seenSync.has(objectId)) kept.push(ev)
  }

  const sorted = kept.sort(
    (a, b) => compareStory(a.at, b.at) || a.title.localeCompare(b.title),
  )
  const prevSorted = [...session.timeline].sort(
    (a, b) => compareStory(a.at, b.at) || a.title.localeCompare(b.title),
  )
  const same =
    sorted.length === prevSorted.length &&
    sorted.every((ev, i) => {
      const prev = prevSorted[i]!
      return (
        ev.id === prev.id &&
        ev.at === prev.at &&
        ev.title === prev.title &&
        ev.summary === prev.summary &&
        ev.iconType === prev.iconType &&
        ev.kind === prev.kind
      )
    })
  if (same) return session
  return { ...session, timeline: sorted }
}

const STALE_HOUR_SUMMARY =
  /one-hour session clock/i

const CANNED_BLANK_SUMMARY =
  /^(Blank board\. Add objects (as you build|and timed events as you build)\.|Empty board\. Place objects from the palette\.)$/i

/** Drop seeded blank / “one-hour” blurbs so they are not treated as a real description. */
export function healSessionCopy(session: ScenarioSession): ScenarioSession {
  const raw = session as ScenarioSession & { markedReady?: unknown }
  let next: ScenarioSession = session
  let changed = false

  // Pre-release: drop void markedReady; do not invent or clear published from it.
  if ('markedReady' in raw) {
    next = { ...session }
    delete (next as { markedReady?: unknown }).markedReady
    changed = true
  }

  if (next.summary && (STALE_HOUR_SUMMARY.test(next.summary) || CANNED_BLANK_SUMMARY.test(next.summary))) {
    next = { ...next, summary: '' }
    changed = true
  }

  if (next.intro && CANNED_BLANK_SUMMARY.test(next.intro)) {
    next = { ...next }
    delete next.intro
    changed = true
  }

  return changed ? next : session
}

function collectStoryInstants(session: ScenarioSession): string[] {
  const times: string[] = [session.storyStartTime]
  if (session.storyEndTime) times.push(session.storyEndTime)
  for (const ev of session.timeline) times.push(ev.at)
  for (const c of session.comments) times.push(c.createdAt)
  for (const beat of session.cameraBeats ?? []) times.push(beat.at)
  for (const obj of session.objects) {
    if (obj.startsAt) times.push(obj.startsAt)
    if (obj.endsAt) times.push(obj.endsAt)
    if (obj.addedAt) times.push(obj.addedAt)
    for (const c of obj.comments) times.push(c.createdAt)
    for (const frame of obj.keyframes ?? []) times.push(frame.at)
    for (const frame of obj.radiusKeyframes ?? []) times.push(frame.at)
    for (const frame of obj.statusKeyframes ?? []) times.push(frame.at)
    for (const frame of obj.visibilityKeyframes ?? []) times.push(frame.at)
    for (const frame of obj.windKeyframes ?? []) times.push(frame.at)
  }
  return times
}

/** Authored beat / object times only — excludes planned storyEndTime envelope. */
function collectContentInstants(session: ScenarioSession): string[] {
  const times: string[] = [session.storyStartTime]
  for (const ev of session.timeline) times.push(ev.at)
  for (const beat of session.cameraBeats ?? []) times.push(beat.at)
  for (const obj of session.objects) {
    if (obj.ignored) continue
    if (obj.startsAt) times.push(obj.startsAt)
    if (obj.endsAt) times.push(obj.endsAt)
    for (const frame of obj.keyframes ?? []) times.push(frame.at)
    for (const frame of obj.radiusKeyframes ?? []) times.push(frame.at)
    for (const frame of obj.statusKeyframes ?? []) times.push(frame.at)
    for (const frame of obj.visibilityKeyframes ?? []) times.push(frame.at)
    // Wind marks stay inside the story window. Counting them here let the first
    // tornado Set replace a planned hour with that one mark.
  }
  return times
}

/** Latest story instant minus scenario start, in ms. Computed, not stored. */
export function sessionSpanMs(session: ScenarioSession): number {
  const start = storyMs(session.storyStartTime)
  let max = start
  for (const iso of collectStoryInstants(session)) max = Math.max(max, storyMs(iso))
  return Math.max(0, max - start)
}

/**
 * Length of what's actually on the timeline / board now (not the planned EndsAt).
 * Includes object keyframes — may extend past the last narrative card.
 */
export function contentSpanMs(session: ScenarioSession): number {
  const start = storyMs(session.storyStartTime)
  let max = start
  for (const iso of collectContentInstants(session)) max = Math.max(max, storyMs(iso))
  return Math.max(0, max - start)
}

/**
 * Story length users expect on the time bar: last timeline card (narrative beats).
 * Keyframes past the last card still resolve on the board at end-state; they do not
 * stretch the scrubber into a confusing “16:00+” past the script.
 */
export function narrativeSpanMs(session: ScenarioSession): number {
  const start = storyMs(session.storyStartTime)
  let max = start
  for (const ev of session.timeline) max = Math.max(max, storyMs(ev.at))
  return Math.max(0, max - start)
}

/** Planned length only when storyEndTime is set (ends − starts). */
export function plannedLengthMs(session: ScenarioSession): number | null {
  if (!session.storyEndTime) return null
  return Math.max(0, storyDeltaMs(session.storyStartTime, session.storyEndTime))
}

export function clockDurationMs(session: ScenarioSession, playheadOffsetMs = 0): number {
  const span = sessionSpanMs(session)
  const planned = plannedLengthMs(session) ?? 0
  const need = span > 0 ? span + 10_000 : 0
  return Math.max(need, planned, playheadOffsetMs)
}

/**
 * Preview / Play / time-bar span: last story card, else the planned end, else 1h.
 * Path waypoints and wind marks are drawn inside that window. They do not
 * change its length.
 */
export function displayDurationMs(session: ScenarioSession): number {
  const narrative = narrativeSpanMs(session)
  if (narrative > 0) return narrative
  const planned = plannedLengthMs(session) ?? 0
  if (planned > 0) return planned
  return 60 * 60_000
}

/** Story time no longer grows a stored duration. Kept so callers can stay in the save path. */
export function withSessionSpan(session: ScenarioSession, _playheadOffsetMs = 0): ScenarioSession {
  return session
}

export function sessionHasClock(session: ScenarioSession): boolean {
  if (session.storyEndTime) return true
  if (session.timeline.length > 0) return true
  return session.objects.some(
    (o) =>
      o.startsAt != null ||
      o.endsAt != null ||
      (o.keyframes != null && o.keyframes.length > 0) ||
      (o.radiusKeyframes != null && o.radiusKeyframes.length > 0) ||
      (o.statusKeyframes != null && o.statusKeyframes.length > 0) ||
      (o.visibilityKeyframes != null && o.visibilityKeyframes.length > 0) ||
      o.loopKeyframes === true ||
      o.addedAt != null,
  )
}

export function commentsVisibleAt<T extends { createdAt: string }>(
  comments: readonly T[],
  playheadIso: string,
): T[] {
  return comments.filter((c) => compareStory(c.createdAt, playheadIso) <= 0)
}

export function findWind<T extends { type: string; ignored?: boolean; ghost?: boolean }>(
  objects: readonly T[],
): T | undefined {
  return objects.find((o) => o.type === 'wind' && !o.ignored && !o.ghost)
}

export function isTornado(typeId: string): boolean {
  return typeId === 'tornado'
}

/** Tornado and hurricane share the local-wind ring, track, and story window. */
export function isLocalStorm(typeId: string | undefined | null): boolean {
  return typeId === 'tornado' || typeId === 'hurricane'
}

export function isPlumeSource(typeId: string): boolean {
  return (
    typeId === 'vapor_cloud' ||
    typeId === 'gas_leak' ||
    typeId === 'leak' ||
    typeId === 'brwn_plume' ||
    typeId === 'hot_zone' ||
    typeId === 'pipeburst' ||
    typeId === 'rupture' ||
    typeId === 'exclusion'
  )
}

export function eventObjectIds(actions: readonly { kind: string; objectId: string }[]): string[] {
  return [...new Set(actions.map((a) => a.objectId))]
}
